import React from 'react';

import getPackage from 'components/packages/getPackage';
import PackageGrid from 'components/packages/PackageGrid';
import packageGridCompatibility from 'components/packages/PackageGrid/compatibilityMap.json';

import pipe from 'lib/pipe';

const {
  packages: GRID_PACKAGES,
  zoneTypeToSize: GRID_SIZES_BY_ZONE_TYPE,
} = packageGridCompatibility;

const applyPackageContext = ({ packages, context }) => {
  const {
    applyAlternateTitleFormat = false,
    isMobileOnlyComponent = false,
  } = context;

  let mirror = true;
  let boxFlexCount = 0;

  const shouldMirror = () => {
    mirror = !mirror;
    return mirror;
  };

  return packages.map((pkg, index) => {
    const props = {};

    const isFirstPackage = index === 0;
    const isUpPackage = pkg.type === 'oneUp'
      || pkg.type === 'twoUp'
      || pkg.type === 'threeUp'
      || pkg.type === 'fourUp';

    if (isFirstPackage && isUpPackage) {
      props.alternateTitleFormat = applyAlternateTitleFormat;
    }

    props.isMobileOnlyComponent = isMobileOnlyComponent;

    if (pkg.type === 'sevenUp' || pkg.type === 'ad') {
      boxFlexCount += 1;
      props.enumerator = boxFlexCount;
    }

    if (pkg.type === 'sevenUp') {
      props.adSlot = 'boxflex';
    }

    if (pkg.type === 'ad') {
      props.slot = pkg.metadata.slot || 'boxflex';
      if (index === packages.length - 1 && !pkg.isCustomAd) {
        props.sticky = true;
      }
    }

    return {
      ...pkg,
      props,
      context: {
        ...context,
        shouldMirror,
        packageIndex: index,
      },
    };
  });
};

const getPackageSections = (packages) => packages.reduce((acc, pkg) => {
  const sections = [...acc];

  // Package Grid lays out compatible packages in a grid
  if (GRID_PACKAGES.includes(pkg.type)) {
    const previousSection = sections.pop();

    // Make sure ads don't split grid
    if (previousSection && previousSection.packages[0].type === 'ad') {
      const sectionBeforePrevious = sections.pop();
      if (!sectionBeforePrevious || !sectionBeforePrevious.isGrid) {
        return [...acc, { isGrid: true, packages: [pkg] }];
      }
      // Move package to previous grid, ad goes below
      return [
        ...sections,
        {
          isGrid: true,
          packages: [
            ...sectionBeforePrevious.packages,
            pkg,
          ],
        },
        previousSection,
      ];
    }

    // If first package in grid, create new section
    if (!previousSection || !previousSection.isGrid) {
      return [...acc, { isGrid: true, packages: [pkg] }];
    }

    // Previous section is grid, modify that section to add this package
    return [
      ...sections,
      {
        isGrid: true,
        packages: [
          ...previousSection.packages,
          pkg,
        ],
      },
    ];
  }

  // Package is not grid-able, should be block-level
  return [...acc, { isGrid: false, packages: [pkg] }];
}, []);

// Flatten package section list into components
const getPackageComponent = (pkg) => getPackage(pkg, pkg.props, pkg.context);
const getPackageComponents = (sections) => sections.reduce((acc, section) => {
  const { isGrid, packages } = section;
  const packageComponents = packages.map(getPackageComponent);

  // Wrap components with grid if grid-able
  let components = packageComponents;
  if (isGrid) {
    // Select global context from first package
    const { railContext, isRailAdjacent } = packages[0].context;
    const isInRail = !!railContext;

    let zoneType = 'normal';
    if (isInRail) zoneType = 'rail';
    if (isRailAdjacent) zoneType = 'railAdjacent';

    const packageGrid = (
      <PackageGrid
        key="grid"
        zoneType={zoneType}
        maxPerRow={GRID_SIZES_BY_ZONE_TYPE[zoneType]}
      >
        {packageComponents}
      </PackageGrid>
    );
    components = [packageGrid];
  }

  return [
    ...acc,
    ...components,
  ];
}, []);

export default (zone, pkgs, context) => {
  const { zonePackageTypes, adsDisabled: curationAdsDisabled, packageAdsDisabled } = context;
  const packagesInZone = pkgs.filter((pkg) => pkg.zone === zone);

  // Disable ads if top-level curation flag is enabled
  // or packages have been individually flagged for ignoring ad logic.
  const adsDisabled = curationAdsDisabled || packageAdsDisabled;

  const packagePipeline = pipe(
    applyPackageContext,
    getPackageSections,
    getPackageComponents,
  );

  // Use passed packages type list, or generate based on packages in this function call
  // FullWidth splices its packages so the full list needs to be passed manually on context
  return packagePipeline({
    packages: packagesInZone,
    context: {
      ...context,
      adsDisabled,
      zone,
      zonePackageTypes: zonePackageTypes || packagesInZone.map((pkg) => pkg.type),
    },
  });
};
